import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { CheckCircle, Email, Home, Download } from '@mui/icons-material';
import {
  API_BASE,
  palette,
  pageBackground,
  cardSx,
  primaryButtonSx,
  outlinedButtonSx,
} from './ticketTheme';
import TicketTermsFooter from './TicketTermsFooter';

const TicketSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ticketId = searchParams.get('ticketId');
  const email = searchParams.get('email');
  const verificationCode = location.state?.verificationCode;
  const eventName = location.state?.eventName;
  const entries = location.state?.entries || [];
  const quantity = location.state?.quantity || 1;

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (email) {
      setShowEmailModal(true);
    }
  }, [email]);

  const handleDownloadTicket = async () => {
    if (!ticketId || !email) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams({ email });
      const pdfUrl = `${API_BASE}/api/tickets/pdf/${encodeURIComponent(ticketId)}?${params.toString()}`;
      const res = await fetch(pdfUrl);
      if (!res.ok) throw new Error('Failed to fetch ticket');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `MYM-Tickets-${ticketId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
    } finally {
      setDownloading(false);
    }
  };

  const InfoBlock = ({ label, value, accent, small }) => (
    <Paper
      elevation={0}
      sx={{
        p: small ? 1.5 : 2,
        mb: small ? 1.5 : 2,
        backgroundColor: palette.surfaceAlt,
        border: `1px solid ${palette.border}`,
        borderRadius: 2,
      }}
    >
      <Typography variant="caption" sx={{ color: palette.textMuted, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700, fontSize: small ? 10 : 12 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          color: accent ? palette.accent : palette.text,
          fontWeight: 800,
          letterSpacing: accent ? 2 : 4,
          fontFamily: 'monospace',
          mt: 0.5,
          fontSize: small ? 14 : 20,
        }}
      >
        {value}
      </Typography>
    </Paper>
  );

  const TicketEntryCard = ({ entry, index, total }) => (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        overflow: 'hidden',
        backgroundColor: palette.surface,
        border: `1px solid ${palette.border}`,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          backgroundColor: palette.accentSoft,
          borderBottom: `1px solid ${palette.accentBorder}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: palette.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
          Ticket {index + 1} of {total}
        </Typography>
        <Typography variant="caption" sx={{ color: palette.textSecondary, fontWeight: 600 }}>
          {entry.typeName}
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <InfoBlock label="Ticket ID" value={entry.entryTicketId} accent small />
        <InfoBlock label="Verification Code" value={entry.verificationCode} small />
      </Box>
    </Paper>
  );

  return (
    <>
      <Box
        sx={{
          ...pageBackground,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            maxWidth: 480,
            width: '100%',
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            borderColor: palette.successBorder,
          }}
        >
          <Box
            sx={{
              width: 84,
              height: 84,
              mx: 'auto',
              mb: 2,
              borderRadius: '50%',
              backgroundColor: palette.successSoft,
              border: `1px solid ${palette.successBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircle sx={{ fontSize: 48, color: palette.success }} />
          </Box>

          <Typography variant="h5" sx={{ color: palette.text, fontWeight: 800, mb: 0.5 }}>
            You&apos;re Going!
          </Typography>

          <Typography variant="body1" sx={{ color: palette.textSecondary, mb: 2 }}>
            {quantity > 1 ? `Your ${quantity} tickets have been confirmed` : 'Your ticket has been confirmed'}
          </Typography>

          {eventName && (
            <Typography variant="body1" sx={{ color: palette.accent, mb: 2, fontWeight: 700 }}>
              {eventName}
            </Typography>
          )}

          <Divider sx={{ borderColor: palette.border, my: 3, borderStyle: 'dashed' }} />

          {entries.length > 0 ? (
            <Box sx={{ textAlign: 'left' }}>
              {entries.map((entry, index) => (
                <TicketEntryCard key={entry.entryTicketId} entry={entry} index={index} total={entries.length} />
              ))}
            </Box>
          ) : (
            <>
              {ticketId && <InfoBlock label="Ticket ID" value={ticketId} accent />}
              {verificationCode && <InfoBlock label="Verification Code" value={verificationCode} />}
            </>
          )}

          {email && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: palette.successSoft,
                borderRadius: 2,
                border: `1px solid ${palette.successBorder}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                <Email sx={{ color: palette.success, mr: 1, fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                  Confirmation sent to
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ color: palette.text, fontWeight: 600, wordBreak: 'break-all' }}>
                {email}
              </Typography>
            </Paper>
          )}

          <Typography variant="body2" sx={{ color: palette.textMuted, mb: 3 }}>
            {entries.length > 1
              ? 'Show each ticket\'s QR code or Ticket ID + Verification Code at the event entrance. Each ticket admits one person.'
              : 'Show the QR code or your Ticket ID + Verification Code at the event entrance.'}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {ticketId && (
              <Button
                variant="contained"
                startIcon={downloading ? <CircularProgress size={18} sx={{ color: palette.onAccent }} /> : <Download />}
                disabled={downloading}
                onClick={handleDownloadTicket}
                sx={{ ...primaryButtonSx, py: 1.25 }}
              >
                {downloading ? 'Downloading...' : quantity > 1 ? 'Download All Tickets (PDF)' : 'Download Ticket (PDF)'}
              </Button>
            )}

            <Button
              variant="outlined"
              startIcon={<Home />}
              onClick={() => navigate('/tickets')}
              sx={outlinedButtonSx}
            >
              Back to Events
            </Button>
          </Box>
        </Paper>

        <TicketTermsFooter sx={{ mt: 3, maxWidth: 480, mx: 'auto' }} />
      </Box>

      <Dialog
        open={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: palette.surface,
            border: `1px solid ${palette.accentBorder}`,
            borderRadius: 3,
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ color: palette.text, fontWeight: 800, textAlign: 'center', pb: 1 }}>
          Check Your Email
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${palette.border}`,
              backgroundColor: palette.surfaceAlt,
              textAlign: 'center',
            }}
          >
            <Email sx={{ fontSize: 40, color: palette.accent, mb: 1.5 }} />
            <Typography variant="body1" sx={{ color: palette.textSecondary, mb: 2 }}>
              {quantity > 1 ? `Your ${quantity} tickets with QR codes will be sent to:` : 'Your ticket and QR code will be sent to:'}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: palette.text,
                fontWeight: 700,
                wordBreak: 'break-all',
                mb: 2,
                px: 1,
              }}
            >
              {email}
            </Typography>
            <Typography variant="body2" sx={{ color: palette.textMuted, lineHeight: 1.6 }}>
              Please double-check that this email address is correct. If it is wrong, you may not
              receive your {quantity > 1 ? 'tickets' : 'ticket'}. Also check your spam or junk folder.
              {quantity > 1 && ' A PDF with all your tickets is attached to the email.'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
          <Button
            variant="contained"
            onClick={() => setShowEmailModal(false)}
            sx={{ ...primaryButtonSx, px: 4 }}
          >
            I&apos;ve Checked — Got It
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicketSuccess;
